/**
 * SessionOrchestrator Context - Manages multiple browser sessions
 * Provides centralized control for spawning, switching, and orchestrating containers
 */

import React, { createContext, useContext, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useSessionStore } from '../stores/sessionStore';
import type { 
  BrowserSession, 
  ContainerConfig, 
  BrowserError
} from '../types/browser.types';

interface SessionOrchestratorContextType {
  // Session Management
  sessions: BrowserSession[];
  activeSession: BrowserSession | undefined;
  createSession: (config?: Partial<ContainerConfig>) => Promise<BrowserSession>;
  terminateSession: (id: string) => Promise<void>;
  switchSession: (id: string) => void;
  duplicateSession: (id: string) => Promise<BrowserSession>;
  
  // Bulk Operations
  terminateAllSessions: () => Promise<void>;
  hibernateInactiveSessions: () => void;
  
  // Session State
  isSpawning: boolean;
  errors: Map<string, BrowserError>;
  getSessionError: (id: string) => BrowserError | undefined;
  
  // Advanced Features
  createTabGroup: (urls: string[], config?: Partial<ContainerConfig>) => Promise<BrowserSession[]>;
  syncSessions: (masterSessionId: string, slaveSessionIds: string[]) => void;
  recordSession: (sessionId: string) => void;
  stopRecording: (sessionId: string) => void;
}

const SessionOrchestratorContext = createContext<SessionOrchestratorContextType | undefined>(undefined);

interface SessionOrchestratorProviderProps {
  children: ReactNode;
  maxConcurrentSessions?: number;
  autoHibernate?: boolean;
  hibernateAfterMs?: number;
  enableRecording?: boolean;
  enableSync?: boolean;
}

export const SessionOrchestratorProvider: React.FC<SessionOrchestratorProviderProps> = ({
  children,
  maxConcurrentSessions = 5,
  autoHibernate = true,
  hibernateAfterMs = 300000, // 5 minutes
  enableRecording = false,
  enableSync = false
}) => {
  const {
    sessions,
    activeSessionId,
    isSpawning,
    errors,
    createSession: storeCreateSession,
    terminateSession: storeTerminateSession,
    switchSession: storeSwitchSession,
    duplicateSession: storeDuplicateSession,
    hibernateSession,
    getAllSessions,
    getActiveSession,
    updateSession
  } = useSessionStore();

  const sessionsArray = getAllSessions();
  const activeSession = getActiveSession();

  // Auto-hibernate inactive sessions
  useEffect(() => {
    if (!autoHibernate) return;

    const checkInactiveSessions = () => {
      const now = Date.now();
      sessionsArray.forEach(session => {
        if (session.id !== activeSessionId && 
            session.status === 'connected' &&
            now - session.lastActivity.getTime() > hibernateAfterMs) {
          hibernateSession(session.id);
          console.log(`🌙 Auto-hibernated inactive session: ${session.id}`);
        }
      });
    };

    const interval = setInterval(checkInactiveSessions, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [sessionsArray, activeSessionId, autoHibernate, hibernateAfterMs, hibernateSession]);

  // Enhanced session creation with validation
  const createSession = useCallback(async (config?: Partial<ContainerConfig>) => {
    if (sessionsArray.length >= maxConcurrentSessions) {
      throw new Error(`Maximum concurrent sessions (${maxConcurrentSessions}) reached`);
    }

    try {
      const session = await storeCreateSession(config);
      console.log(`🚀 Created new session: ${session.id}`);
      return session;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }, [sessionsArray.length, maxConcurrentSessions, storeCreateSession]);

  // Enhanced session termination with cleanup
  const terminateSession = useCallback(async (id: string) => {
    console.log(`🔴 Terminating session: ${id}`);
    await storeTerminateSession(id);
  }, [storeTerminateSession]);

  // Terminate all sessions
  const terminateAllSessions = useCallback(async () => {
    console.log('🔴 Terminating all sessions...');
    const promises = sessionsArray.map(session => terminateSession(session.id));
    await Promise.all(promises);
  }, [sessionsArray, terminateSession]);

  // Hibernate inactive sessions
  const hibernateInactiveSessions = useCallback(() => {
    sessionsArray.forEach(session => {
      if (session.id !== activeSessionId && session.status === 'connected') {
        hibernateSession(session.id);
      }
    });
  }, [sessionsArray, activeSessionId, hibernateSession]);

  // Create tab group (multiple sessions with different URLs)
  const createTabGroup = useCallback(async (
    urls: string[], 
    config?: Partial<ContainerConfig>
  ): Promise<BrowserSession[]> => {
    const sessions: BrowserSession[] = [];
    
    for (const url of urls) {
      try {
        const session = await createSession({
          ...config,
          env: {
            ...config?.env,
            INITIAL_URL: url
          }
        });
        sessions.push(session);
        
        // Add delay between spawns to avoid overload
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`Failed to create session for ${url}:`, error);
      }
    }
    
    return sessions;
  }, [createSession]);

  // Sync sessions (master-slave model)
  const syncSessions = useCallback((masterSessionId: string, slaveSessionIds: string[]) => {
    if (!enableSync) {
      console.warn('Session sync is disabled');
      return;
    }

    console.log(`🔄 Syncing sessions: Master ${masterSessionId} -> Slaves ${slaveSessionIds.join(', ')}`);
    
    // In production, this would establish WebRTC data channels between containers
    // For now, we just update metadata
    slaveSessionIds.forEach(slaveId => {
      updateSession(slaveId, {
        metadata: {
          ...sessions.get(slaveId)?.metadata,
          syncMaster: masterSessionId
        } as any
      });
    });
  }, [enableSync, sessions, updateSession]);

  // Session recording
  const recordSession = useCallback((sessionId: string) => {
    if (!enableRecording) {
      console.warn('Session recording is disabled');
      return;
    }

    console.log(`🔴 Started recording session: ${sessionId}`);
    
    // In production, this would start recording via MediaRecorder API
    updateSession(sessionId, {
      metadata: {
        ...sessions.get(sessionId)?.metadata,
        isRecording: true,
        recordingStartTime: new Date()
      } as any
    });
  }, [enableRecording, sessions, updateSession]);

  const stopRecording = useCallback((sessionId: string) => {
    if (!enableRecording) return;

    console.log(`⏹️ Stopped recording session: ${sessionId}`);
    
    updateSession(sessionId, {
      metadata: {
        ...sessions.get(sessionId)?.metadata,
        isRecording: false,
        recordingEndTime: new Date()
      } as any
    });
  }, [enableRecording, sessions, updateSession]);

  // Get session error
  const getSessionError = useCallback((id: string) => {
    return errors.get(id);
  }, [errors]);

  const contextValue: SessionOrchestratorContextType = {
    sessions: sessionsArray,
    activeSession,
    createSession,
    terminateSession,
    switchSession: storeSwitchSession,
    duplicateSession: storeDuplicateSession,
    terminateAllSessions,
    hibernateInactiveSessions,
    isSpawning,
    errors,
    getSessionError,
    createTabGroup,
    syncSessions,
    recordSession,
    stopRecording
  };

  return (
    <SessionOrchestratorContext.Provider value={contextValue}>
      {children}
    </SessionOrchestratorContext.Provider>
  );
};

// Custom hook for using the orchestrator
export const useSessionOrchestrator = () => {
  const context = useContext(SessionOrchestratorContext);
  if (!context) {
    throw new Error('useSessionOrchestrator must be used within SessionOrchestratorProvider');
  }
  return context;
};

// Utility hook for current active session
export const useActiveSession = () => {
  const { activeSession } = useSessionOrchestrator();
  return activeSession;
};

// Utility hook for session errors
export const useSessionErrors = () => {
  const { errors, getSessionError } = useSessionOrchestrator();
  return { errors, getSessionError };
};
